<script setup lang="ts">
import {
  PHASE_GET_SOURCES,
  getSourceOptions,
  mathOpOptions,
  registerOptions,
  registerOrLiteralOptions,
  setTargetOptions,
} from '../../model/inspectorOptions'
import { useFlowEditorContext } from '../../composables/useFlowEditor'
import type { FlowNode } from '../../model/types'

/** get-value, set-value and math-op register actions. */
defineProps<{ selectedNode: FlowNode }>()

const editor = useFlowEditorContext()
const { debounced: di, flush: db } = editor.inspector
const {
  updateGetSource,
  updateGetLit,
  updateGetPhase,
  updateGetRegister,
  updateSetTarget,
  updateSetInput,
  updateSetLit,
  updateMathTarget,
  updateMathOp,
  updateMathOperand,
  updateMathLit,
} = editor.inspector
</script>

<template>
  <!-- get-value -->
  <template v-if="selectedNode.type === 'action' && selectedNode.data.actionType === 'get-value'">
    <label>
      <span>{{ $t('flow.readFrom') }}</span>
      <select
        :value="selectedNode.data.getSource ?? 'group-size'"
        @change="updateGetSource(($event.target as HTMLSelectElement).value)"
      >
        <option v-for="s in getSourceOptions" :key="s.value" :value="s.value">
          {{ s.label }}
        </option>
      </select>
    </label>
    <label v-if="selectedNode.data.getSource === 'literal'">
      <span>{{ $t('flow.literalValue') }}</span>
      <input
        type="number"
        :value="selectedNode.data.getLit ?? 0"
        @input="di(updateGetLit)($event)"
        @blur="db(updateGetLit)($event)"
      />
    </label>
    <label v-if="PHASE_GET_SOURCES.includes(selectedNode.data.getSource ?? '')">
      <span>{{ $t('flow.phaseIdOptional') }}</span>
      <input
        type="text"
        placeholder="phase-2"
        :value="selectedNode.data.getPhase ?? ''"
        @input="di(updateGetPhase)($event)"
        @blur="db(updateGetPhase)($event)"
      />
    </label>
    <label>
      <span>{{ $t('flow.storeInRegister') }}</span>
      <select
        :value="selectedNode.data.getRegister ?? 'R1'"
        @change="updateGetRegister(($event.target as HTMLSelectElement).value)"
      >
        <option v-for="r in registerOptions" :key="r.value" :value="r.value">
          {{ r.label }}
        </option>
      </select>
    </label>
  </template>

  <!-- set-value -->
  <template v-if="selectedNode.type === 'action' && selectedNode.data.actionType === 'set-value'">
    <label>
      <span>{{ $t('flow.setTarget') }}</span>
      <select
        :value="selectedNode.data.setTarget ?? 'progress-total'"
        @change="updateSetTarget(($event.target as HTMLSelectElement).value)"
      >
        <option v-for="t in setTargetOptions" :key="t.value" :value="t.value">
          {{ t.label }}
        </option>
      </select>
    </label>
    <label>
      <span>{{ $t('flow.input') }}</span>
      <select
        :value="selectedNode.data.setInput ?? 'R1'"
        @change="updateSetInput(($event.target as HTMLSelectElement).value)"
      >
        <option v-for="r in registerOrLiteralOptions" :key="r.value" :value="r.value">
          {{ r.label }}
        </option>
      </select>
    </label>
    <label v-if="selectedNode.data.setInput === 'literal'">
      <span>{{ $t('flow.literalValue') }}</span>
      <input
        type="number"
        :value="selectedNode.data.setLit ?? 0"
        @input="di(updateSetLit)($event)"
        @blur="db(updateSetLit)($event)"
      />
    </label>
  </template>

  <!-- math-op -->
  <template v-if="selectedNode.type === 'action' && selectedNode.data.actionType === 'math-op'">
    <label>
      <span>{{ $t('flow.targetRegister') }}</span>
      <select
        :value="selectedNode.data.mathTarget ?? 'R1'"
        @change="updateMathTarget(($event.target as HTMLSelectElement).value)"
      >
        <option v-for="r in registerOptions" :key="r.value" :value="r.value">
          {{ r.label }}
        </option>
      </select>
    </label>
    <label>
      <span>{{ $t('flow.operation') }}</span>
      <select
        :value="selectedNode.data.mathOp ?? '+1'"
        @change="updateMathOp(($event.target as HTMLSelectElement).value)"
      >
        <option v-for="op in mathOpOptions" :key="op.value" :value="op.value">
          {{ op.label }}
        </option>
      </select>
    </label>
    <!-- +n / -n: just a literal number input, no operand source selector -->
    <label v-if="selectedNode.data.mathOp === '+n' || selectedNode.data.mathOp === '-n'">
      <span>{{ $t('flow.nLiteralValue') }}</span>
      <input
        type="number"
        :value="selectedNode.data.mathLit ?? 1"
        @input="di(updateMathLit)($event)"
        @blur="db(updateMathLit)($event)"
      />
    </label>
    <!-- add/sub/mul/set: full operand selector -->
    <template
      v-if="
        selectedNode.data.mathOp !== '+1' &&
        selectedNode.data.mathOp !== '-1' &&
        selectedNode.data.mathOp !== '+n' &&
        selectedNode.data.mathOp !== '-n'
      "
    >
      <label>
        <span>{{ $t('flow.operand') }}</span>
        <select
          :value="selectedNode.data.mathOperand ?? 'literal'"
          @change="updateMathOperand(($event.target as HTMLSelectElement).value)"
        >
          <option v-for="r in registerOrLiteralOptions" :key="r.value" :value="r.value">
            {{ r.label }}
          </option>
        </select>
      </label>
      <label v-if="selectedNode.data.mathOperand === 'literal' || !selectedNode.data.mathOperand">
        <span>{{ $t('flow.literalValue') }}</span>
        <input
          type="number"
          :value="selectedNode.data.mathLit ?? 1"
          @input="di(updateMathLit)($event)"
          @blur="db(updateMathLit)($event)"
        />
      </label>
    </template>
  </template>
</template>

<style scoped src="../styles/inspectorForm.css"></style>
